/* Nude Project — Countdown Banner
   ---------------------------------------------------------------------------
   Custom element <countdown-banner>. Self-contained by design:

   NO DOCUMENT ACCESS. Every node this element touches is read through
   `this.querySelector`, never `document.*`. assets/collection-password-v2.js
   does the opposite (lines 89 and 108 hold a `document.querySelector` result
   with no null guard) and throws a TypeError on any page without a
   `.collection-password-v2` node. This element can be dropped on any template
   and will never reach outside itself.

   IIFE + `customElements.get` GUARD. The <script> tag lives inside the section,
   so two instances of the section on one page emit two identical tags and the
   file executes twice. A bare top-level `class` declaration would throw
   "Identifier has already been declared" on the second pass, and
   customElements.define would throw NotSupportedError. This is not
   hypothetical: collection-password-v2.js is loaded from BOTH
   layout/theme.liquid:388 and sections/collection-password-v2.liquid:130 and
   already double-executes on the password-v2 template.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (customElements.get('countdown-banner')) return;

  var DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
  var TIME_RE = /^(\d{2}):(\d{2})$/;

  var SECOND_MS = 1000;
  var MINUTE_MS = 60 * SECOND_MS;
  var HOUR_MS = 60 * MINUTE_MS;
  var DAY_MS = 24 * HOUR_MS;

  class CountdownBanner extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.target = null;
      this.nodes = null;
      this.onTick = this.tick.bind(this);
    }

    connectedCallback() {
      // A move within the DOM fires disconnected + connected. Clearing first
      // makes a second interval impossible under any ordering.
      this.stop();

      this.cacheNodes();
      if (!this.nodes) {
        this.setState('invalid');
        return;
      }

      this.target = this.readTarget();
      if (this.target === null) {
        // Deliberately NOT painting 00:00:00:00 — zeros read as "already
        // launched". A misconfigured date hides the row instead.
        this.setState('invalid');
        return;
      }

      this.setState('live');
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    cacheNodes() {
      var days = this.querySelector('[data-countdown="days"]');
      var hours = this.querySelector('[data-countdown="hours"]');
      var minutes = this.querySelector('[data-countdown="minutes"]');
      var seconds = this.querySelector('[data-countdown="seconds"]');

      this.nodes =
        days && hours && minutes && seconds
          ? { days: days, hours: hours, minutes: minutes, seconds: seconds }
          : null;
    }

    setState(state) {
      this.dataset.state = state;
    }

    readTarget() {
      var parts = CountdownBanner.parseWallClock(this.dataset.date, this.dataset.time);
      if (!parts) return null;
      return CountdownBanner.epochFromZoned(parts, this.dataset.timezone || 'UTC');
    }

    /* Strict parse of the two merchant-facing text settings.
       Returns the wall-clock fields, or null if anything is off. */
    static parseWallClock(dateStr, timeStr) {
      if (typeof dateStr !== 'string' || typeof timeStr !== 'string') return null;

      var d = DATE_RE.exec(dateStr.trim());
      var t = TIME_RE.exec(timeStr.trim());
      if (!d || !t) return null;

      var parts = {
        y: Number(d[1]),
        mo: Number(d[2]),
        d: Number(d[3]),
        h: Number(t[1]),
        mi: Number(t[2])
      };

      if (parts.mo < 1 || parts.mo > 12) return null;
      if (parts.d < 1 || parts.d > 31) return null;
      if (parts.h > 23) return null;
      if (parts.mi > 59) return null;

      return parts;
    }

    /* Offset of `timeZone` at the instant `utcMs`, in milliseconds.
       Formats the instant in the zone, rebuilds it as if those wall-clock
       fields were UTC, and takes the difference. */
    static offsetOf(utcMs, timeZone) {
      var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      var map = {};
      var list = dtf.formatToParts(new Date(utcMs));
      for (var i = 0; i < list.length; i++) map[list[i].type] = list[i].value;

      // Some engines emit "24" for midnight under hour12:false, on the same
      // calendar day that 00 would carry. Normalise it.
      var hour = Number(map.hour);
      if (hour === 24) hour = 0;

      var asUTC = Date.UTC(
        Number(map.year),
        Number(map.month) - 1,
        Number(map.day),
        hour,
        Number(map.minute),
        Number(map.second)
      );

      return asUTC - utcMs;
    }

    /* Wall-clock in a named zone -> epoch ms.

       Two passes. The first offset is measured at the wrong instant (the
       wall-clock read as UTC), which lands on the wrong side of a DST
       transition when the target sits near one. Re-measuring at the corrected
       instant fixes it. Verified by hand against Europe/Madrid:
         2026-01-15 20:00 CET          -> 19:00Z
         2026-03-29 03:00 CEST (spring)-> 01:00Z
         2026-10-25 02:30 (ambiguous)  -> 01:30Z, the second occurrence */
    static epochFromZoned(parts, timeZone) {
      if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
        return null;
      }

      try {
        var guess = Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, 0);
        if (!isFinite(guess)) return null;

        var offset = CountdownBanner.offsetOf(guess, timeZone);
        var offset2 = CountdownBanner.offsetOf(guess - offset, timeZone);
        var target = guess - offset2;

        return isFinite(target) ? target : null;
      } catch (e) {
        // Unknown time zone, or no Intl time-zone data in this engine.
        return null;
      }
    }

    start() {
      // Immediate paint before the interval, so there is no flash of 00.
      this.tick();

      // tick() may have ended the countdown already; only arm the interval if
      // it is still running.
      if (this.dataset.state === 'live') {
        this.timer = setInterval(this.onTick, SECOND_MS);
      }
    }

    stop() {
      if (this.timer !== null) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    tick() {
      var diff = this.target - Date.now();

      if (diff <= 0) {
        this.paint(0, 0, 0, 0);
        this.setState('ended');
        this.stop();
        this.applyAtZero();
        return;
      }

      this.paint(
        Math.floor(diff / DAY_MS),
        Math.floor((diff % DAY_MS) / HOUR_MS),
        Math.floor((diff % HOUR_MS) / MINUTE_MS),
        Math.floor((diff % MINUTE_MS) / SECOND_MS)
      );
    }

    /* What happens the instant the countdown hits zero.

       Every value read here is read live, at call time: data-at-zero,
       data-design-mode, the ancestor lookup and window.applyHeaderLogic. Nothing
       is captured during connectedCallback. That is what makes the transition
       identical whether zero is crossed on arrival or with the page already
       open — the path depends on nothing that only happens at load. */
    applyAtZero() {
      // Theme editor: never hide anything, or the merchant loses the section
      // they are trying to edit. The flag is printed by the Liquid, so it holds
      // even if this file had failed to load.
      if (this.dataset.designMode === 'true') return;

      // "Hide the countdown only" is pure CSS from here: the
      // [data-state="ended"] rule in countdown-banner.css does the rest and the
      // surrounding banner stays exactly as it is.
      if (this.dataset.atZero !== 'hide_section') return;

      // Ancestor lookup on this element, not a document query — the rule at the
      // top of this file still holds.
      var root = this.closest('.countdown-banner__root');
      if (!root) return;

      root.dataset.countdownHidden = 'true';

      // The banner is gone, so it must stop claiming a transparent header.
      // These two are exactly what applyHeaderLogic() reads (global.js:2376).
      root.classList.remove('header-version');
      root.removeAttribute('data-header-version');

      /* DELIBERATE, APPROVED EXCEPTION to this file's no-document-access rule.
         Reviewed and signed off — not an oversight.

         Dropping the class is not enough on its own. applyHeaderLogic only runs
         on DOMContentLoaded (global.js:2396) plus two theme-editor-only events
         (global.js:2399-2400), so when zero is crossed with the page already
         open it ran long ago and the header would stay transparent over
         whatever section is now first.

         Calling it again is safe. It is idempotent: global.js:2373 clears both
         transparent classes before doing anything else, so re-running it with no
         .header-version left in the first section resets the header to solid.
         It also self-guards on a missing header at global.js:2370. And the
         typeof check below removes the very failure mode the no-document-access
         rule exists to prevent: if the function is ever renamed or wrapped in an
         IIFE, this degrades silently instead of throwing.

         A synthetic 'shopify:section:load' was considered and rejected. Seven
         listeners ride that event and three of them consume event.target
         (cart-related-products.js:159, animations.js:100,
         header-account-nav.js:32); dispatching it on document would hand them
         document itself and re-run cart fetches and every scroll animation on
         the page. */
      if (typeof window.applyHeaderLogic === 'function') {
        window.applyHeaderLogic();
      }
    }

    /* padStart pads, never truncates: 120 days renders as "120", not "20". */
    paint(days, hours, minutes, seconds) {
      if (!this.nodes) return;
      this.nodes.days.textContent = String(days).padStart(2, '0');
      this.nodes.hours.textContent = String(hours).padStart(2, '0');
      this.nodes.minutes.textContent = String(minutes).padStart(2, '0');
      this.nodes.seconds.textContent = String(seconds).padStart(2, '0');
    }
  }

  customElements.define('countdown-banner', CountdownBanner);
})();
